import React, { useCallback, useEffect, useState } from 'react';
import CommonButton from '../../../Components/CommonButton';
import CommonLoader from '../../../Components/CommonLoader';
import CommonTable from '../../../Components/CommonTable';
import CommonActionMenu from '../../../Components/CommonActionMenu';
import StatusBadge from '../../../Components/StatusBadge';
import NoDataPage from '../../../Components/NoDataPage';
import { useToast } from '../../../Components/CommonToast';
import { useConfirm } from '../../../Components/ConfirmModal';
import { heading3, bodySmall, fontSize, Body } from '../../../Constants/Fonts';
import AddOnCard from '../../../Dashboard/AddOns/AddOnCard';
import {
    risk_portal_bus_list,
    risk_portal_bu_breakdown,
    risk_portal_bu_update,
    risk_portal_bu_delete,
} from '../../../services';
import BusinessUnitForm from '../Forms/BusinessUnitForm';
import PageHeading from '../../../Components/PageHeading';

const unwrap = (res) =>
    res?.data?.response_data?.data ?? res?.data?.data ??
    res?.statusCode?.response_data?.data ?? res?.statusCode?.data ?? null;

const HEADERS = ['Name', 'Location', 'Risk Tier', 'Risks (Subtree)', 'By Severity', 'Status', 'Actions'];

const TIER_TOKEN = { low: '--color-low', medium: '--color-medium', high: '--color-high', critical: '--color-critical' };

// Flatten the parent_id tree into ordered rows carrying their depth (root
// children first, each followed by its descendants).
const flattenTree = (bus) => {
    const childrenOf = {};
    bus.forEach((b) => {
        const p = b.parent_id || 'root';
        (childrenOf[p] = childrenOf[p] || []).push(b);
    });
    const out = [];
    const walk = (list, depth) => (list || []).forEach((b) => { out.push({ bu: b, depth }); walk(childrenOf[b.id], depth + 1); });
    walk(childrenOf.root, 0);
    // Any BU whose parent isn't in the list (orphan) still shows at root level.
    bus.forEach((b) => { if (!out.some((o) => o.bu.id === b.id)) out.push({ bu: b, depth: 0 }); });
    return out;
};

const sev = (n, token, suffix) => (
    <>
        <Body as="span" color={`var(${token})`}>{n || 0} {suffix}</Body>
    </>
);

// BusinessUnits — hierarchical BU CRUD. Owns the data (bus_list + bu_breakdown),
// the org-total summary, the mutations, and the row rendering (inlined below);
// the Add/Edit modal lives in Forms/BusinessUnitForm.
// canManage = risk_admin | risk_manager (matches the reference sidebar).
export default function BusinessUnits({ role = 'risk_admin' }) {
    const [bus, setBus] = useState([]);
    const [countsById, setCountsById] = useState({});
    const [orgCounts, setOrgCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);

    const { notify, toastNode } = useToast();
    const { confirm, confirmModal } = useConfirm();
    const canManage = role === 'risk_admin' || role === 'risk_manager';

    const load = useCallback(async () => {
        try {
            const [listRes, bdRes] = await Promise.allSettled([
                risk_portal_bus_list(),
                risk_portal_bu_breakdown({ rollup: 'true' }),
            ]);
            const listD = listRes.status === 'fulfilled' ? unwrap(listRes.value) : null;
            const bd = bdRes.status === 'fulfilled' ? unwrap(bdRes.value) : null;
            setBus(Array.isArray(listD?.business_units) ? listD.business_units : Array.isArray(listD) ? listD : []);
            setCountsById(Object.fromEntries((bd?.business_units || []).map((b) => [b.id, b])));
            setOrgCounts(bd?.organization_level?.counts || {});
        } catch (e) {
            notify('Failed to load business units', 'error');
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => { setLoading(true); load(); }, [load]);

    const op = async (fn, okMsg) => {
        setBusy(true);
        try {
            await fn();
            if (okMsg) notify(okMsg, 'success');
            await load();
        } catch (e) {
            notify(e?.response?.data?.error?.message || e?.message || 'Action failed', 'error');
        } finally {
            setBusy(false);
        }
    };

    const openAdd = () => { setEditing(null); setShowForm(true); };
    const openEdit = (bu) => { setEditing(bu); setShowForm(true); };
    const toggle = (bu) => op(() => risk_portal_bu_update(bu.id, { is_active: bu.is_active === false }), bu.is_active === false ? 'Business unit activated' : 'Business unit deactivated');
    const remove = async (bu) => {
        const ok = await confirm({
            title: 'Delete business unit',
            message: `Delete "${bu.name}"? This can't be undone if no risks or sub-units depend on it.`,
            confirmLabel: 'Delete', danger: true, themeColor: false,
        });
        if (!ok) return;
        op(() => risk_portal_bu_delete(bu.id), 'Business unit deleted');
    };

    // Org-total summary (matches the reference): direct risks across all BUs +
    // org-level (no BU) risks.
    const acrossBUs = Object.values(countsById).reduce((acc, b) => acc + (b.counts_direct?.total || 0), 0);
    const orgOnly = orgCounts.total || 0;
    const totalRisks = acrossBUs + orgOnly;

    const rows = flattenTree(bus).map(({ bu, depth }) => {
        const counts = countsById[bu.id]?.counts_with_descendants || {};
        const direct = countsById[bu.id]?.counts_direct || { total: 0 };
        const tierColor = `var(${TIER_TOKEN[bu.risk_tier] || '--color-text-muted'})`;
        const active = bu.is_active !== false;
        return {
            data: [
                <Body as="span" style={{ display: 'inline-flex', alignItems: 'center', paddingLeft: depth * 18, opacity: active ? 1 : 0.55 }}>
                    {depth > 0 && <Body as="span" color="var(--color-text-muted)" style={{ marginRight: 6 }}>↳</Body>}
                    <Body as="span" color="var(--color-text-primary)" weight="semibold">{bu.name}</Body>
                    {bu.code && <Body as="span" variant={2} color="var(--color-text-muted)" style={{ marginLeft: 6 }}>({bu.code})</Body>}
                </Body>,
                <Body as="span" variant={2} color="var(--color-text-secondary)">{bu.location || '—'}</Body>,
                <Body as="span" variant={2} weight="medium" style={{
                    display: 'inline-block', padding: '2px 10px', borderRadius: 'var(--radius-pill)',
                    background: tierColor, color: 'var(--color-text-inverse)', textTransform: 'capitalize',
                }}>{bu.risk_tier || 'medium'}</Body>,
                <Body as="span" variant={2} color="var(--color-text-primary)">
                    <strong>{counts.total || 0}</strong>
                    <Body as="span" color="var(--color-text-muted)" style={{ marginLeft: 4 }}>({direct.total || 0} direct)</Body>
                </Body>,
                <Body as="span" variant={2} style={{ whiteSpace: 'nowrap' }}>
                    {sev(counts.critical, '--color-critical', 'crit')}
                    <Body as="span" color="var(--color-text-muted)"> / </Body>
                    {sev(counts.high, '--color-high', 'high')}
                    <Body as="span" color="var(--color-text-muted)"> / </Body>
                    {sev(counts.medium, '--color-medium', 'med')}
                    <Body as="span" color="var(--color-text-muted)"> / </Body>
                    {sev(counts.low, '--color-low', 'low')}
                </Body>,
                <StatusBadge status={active ? 'active' : 'inactive'} />,
                canManage ? (
                    <CommonActionMenu
                        actions={[
                            { label: 'Edit', onClick: () => openEdit(bu) },
                            { label: active ? 'Deactivate' : 'Activate', onClick: () => toggle(bu) },
                            { label: 'Delete', onClick: () => remove(bu), danger: true },
                        ]}
                    />
                ) : <Body as="span" variant={2} color="var(--color-text-muted)">—</Body>,
            ],
        };
    });

    if (loading) return <CommonLoader fullPage label="Loading business units…" />;

    return (
        <div className="rp1_widget">
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                <PageHeading
                    title='Business Units'
                    subtitle=" Hierarchical structure: organization → business units → sub-units. Risks link to a BU; subtree filtering rolls counts up to the parent."
                />
                {canManage && <CommonButton text="Add Business Unit" onClick={openAdd} />}
            </div>

            {/* Organization total summary — one AddOnCard tile per metric */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, margin: '14px 0' }}>
                <AddOnCard

                    icon="risk"
                    label="Total Risks"
                    action={<Body as="span" weight="bold" color="var(--color-text-primary)" style={{ fontSize: fontSize['3xl'] }}>{totalRisks}</Body>}
                />
                <AddOnCard
                    icon="layers"
                    label="Org-level (no BU)"
                    action={<Body as="span" weight="bold" color="var(--color-text-primary)" style={{ fontSize: fontSize['3xl'] }}>{orgOnly}</Body>}
                />
                <AddOnCard
                    icon="building"
                    label={`Across ${bus.length} BU${bus.length === 1 ? '' : 's'}`}
                    action={<Body as="span" weight="bold" color="var(--color-text-primary)" style={{ fontSize: fontSize['3xl'] }}>{acrossBUs}</Body>}
                />
            </div>

            {bus.length === 0 ? (
                <NoDataPage hint="No business units yet. Click Add Business Unit to create the first." />
            ) : (
                <CommonTable headers={HEADERS} rows={rows} noPadding noScroll />
            )}

            {showForm && (
                <BusinessUnitForm
                    key={editing?.id || 'new'}
                    show
                    editing={editing}
                    businessUnits={bus}
                    onClose={() => setShowForm(false)}
                    onSuccess={(msg) => { notify(msg, 'success'); load(); }}
                    onError={(msg) => notify(msg, 'error')}
                />
            )}

            {busy && <CommonLoader fullPage label="Working…" />}
            {confirmModal}
            {toastNode}
        </div>
    );
}
